import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IS_MOBILE } from '@/lib/device';

// Lazy-loaded GSAP reference to avoid pulling it into the initial bundle
let gsapPromise: Promise<typeof import('gsap')> | null = null;
const getGsap = () => {
  if (!gsapPromise) gsapPromise = import('gsap');
  return gsapPromise.then(m => m.default);
};

interface TransitionContextType {
  navigateWithTransition: (to: string, color?: string) => void;
  isTransitioning: boolean;
  transitionPhase: 'idle' | 'entering' | 'exiting';
}

const TransitionContext = createContext<TransitionContextType | null>(null);

export const usePageTransition = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error('usePageTransition must be used within PageTransitionProvider');
  }
  return context;
};

// Helper to get contrast color for text
function getContrastColor(hexcolor: string): string {
  if (!hexcolor || hexcolor.length < 7) return '#ffffff';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export const PageTransitionProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<'idle' | 'entering' | 'exiting'>('idle');

  // Circle transition refs (for Cases)
  const overlayRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [transitionColor, setTransitionColor] = useState('#ffffff');
  const [transitionText, setTransitionText] = useState('');

  const navigateWithTransition = useCallback((to: string, color?: string) => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setTransitionPhase('entering');

    if (!color) {
      navigate(to);
      setIsTransitioning(false);
      setTransitionPhase('idle');
      return;
    }

    // ========== CIRCLE + TEXT TRANSITION (Cases) ==========
    setTransitionColor(color);

    // Extract text for transition
    const pathParts = to.split('/');
    let displayText = pathParts[pathParts.length - 1];
    if (displayText === '' || displayText.startsWith('#')) {
      displayText = 'Home';
    } else {
      displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
    }
    setTransitionText(displayText);

    getGsap().then(gsap => {
      const tl = gsap.timeline({
        onComplete: () => {
          navigate(to);
          setTimeout(() => {
            playExitAnimation();
          }, 50);
        }
      });

      // Show overlay
      if (overlayRef.current) {
        gsap.set(overlayRef.current, { display: 'block' });
      }

      // Circle expand from center
      if (circleRef.current) {
        tl.fromTo(circleRef.current,
          { scale: 0, opacity: 1 },
          { scale: 3, duration: 0.7, ease: 'power3.inOut' }
        );
      }

      // Text animation - 3D character reveal
      if (textRef.current) {
        const chars = textRef.current.querySelectorAll('.char');
        tl.fromTo(chars,
          { opacity: 0, y: 100, rotateX: -90 },
          { opacity: 1, y: 0, rotateX: 0, duration: 0.5, stagger: 0.03, ease: 'back.out(1.7)' },
          '-=0.4'
        );
      }
    });
  }, [navigate, isTransitioning]);

  const playExitAnimation = useCallback(() => {
    getGsap().then(gsap => {
      const tl = gsap.timeline({
        onComplete: () => {
          setIsTransitioning(false);
          setTransitionPhase('idle');
          if (overlayRef.current) {
            gsap.set(overlayRef.current, { display: 'none' });
          }
          // Scroll to hash if present
          const hash = window.location.hash;
          if (hash) {
            setTimeout(() => {
              const element = document.querySelector(hash);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        }
      });

      // Text exit - fly up
      if (textRef.current) {
        const chars = textRef.current.querySelectorAll('.char');
        tl.to(chars, {
          opacity: 0,
          y: -50,
          rotateX: 45,
          duration: 0.3,
          stagger: 0.02,
          ease: 'power3.in',
        });
      }

      // Circle shrink and fade
      if (circleRef.current) {
        tl.to(circleRef.current, {
          scale: 0,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.inOut',
        }, '-=0.2');
      }
    });
  }, []);

  // Initial page load animation - only animate if target element exists and not on mobile
  useEffect(() => {
    // Skip GSAP animations on mobile to avoid DOM conflicts
    if (IS_MOBILE) return;

    const pageContent = document.querySelector('.page-content');
    if (pageContent) {
      getGsap().then(gsap => {
        gsap.fromTo(
          pageContent,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', delay: 0.1 }
        );
      });
    }
  }, [location.pathname]);

  // Split text into characters for 3D animation
  const splitText = (text: string) => {
    return text.split('').map((char, i) => (
      <span
        key={i}
        className="char inline-block"
        style={{ transformStyle: 'preserve-3d', perspective: '500px' }}
      >
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  };

  return (
    <TransitionContext.Provider value={{ navigateWithTransition, isTransitioning, transitionPhase }}>
      {children}

      {/* Circle + Text Transition Overlay (Cases) */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
        style={{ display: 'none' }}
      >
        {/* Expanding circle */}
        <div
          ref={circleRef}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vmax] h-[150vmax] rounded-full"
          style={{
            backgroundColor: transitionColor,
            transform: 'translate(-50%, -50%) scale(0)',
          }}
        />

        {/* Center text with character animation */}
        <div
          ref={textRef}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ perspective: '1000px' }}
        >
          <span
            className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tight"
            style={{
              color: getContrastColor(transitionColor),
              transformStyle: 'preserve-3d',
            }}
          >
            {splitText(transitionText)}
          </span>
        </div>
      </div>
    </TransitionContext.Provider>
  );
};

export default PageTransitionProvider;
